import { useMemo } from "react";
import { useIntl } from "react-intl";

import { IconCheck } from "@/components/Icons";

import type { ExtractedFlightFareDetails } from "../extractDataFromFlightFare";
import {
  FlightFareCardSection,
  FlightFareCardSectionItem,
} from "../FlightFareCardSection";

type FlightFareOtherBenefitsInfoProps = {
  loyaltyProgram: ExtractedFlightFareDetails["loyaltyProgram"];
  priority: ExtractedFlightFareDetails["priority"];
  loungeAccess: ExtractedFlightFareDetails["loungeAccess"];
};

export function FlightFareOtherBenefitsInfo({
  loyaltyProgram,
  priority,
  loungeAccess,
}: FlightFareOtherBenefitsInfoProps) {
  const intl = useIntl();

  const priorityText = useMemo(() => {
    if (!priority) return null;

    switch (priority.type) {
      case "priority_boarding":
        return intl.formatMessage({
          defaultMessage: "Priority boarding",
          description:
            "Text indicating that a flight fare includes priority boarding as a benefit",
        });

      case "priority_check_in":
        return intl.formatMessage({
          defaultMessage: "Priority check-in",
          description:
            "Text indicating that a flight fare includes priority check-in as a benefit",
        });

      case "priority_baggage":
        return intl.formatMessage({
          defaultMessage: "Priority baggage handling",
          description:
            "Text indicating that a flight fare includes priority baggage handling as a benefit",
        });

      case "fast_track":
        return intl.formatMessage({
          defaultMessage: "Fast track security",
          description:
            "Text indicating that a flight fare includes fast track security as a benefit",
        });

      default:
        return null;
    }
  }, [priority, intl]);

  return (
    <FlightFareCardSection
      title={intl.formatMessage({
        defaultMessage: "Other benefits",
        description:
          "Section title for other benefits in fare card such as loyalty program",
      })}
    >
      {!!loyaltyProgram && (
        <FlightFareCardSectionItem
          label={intl.formatMessage(
            {
              defaultMessage:
                "{programName} {hasPercentage, select, true {<b>{percentage}%</b>} other {}}",
              description:
                "Text indicating the loyalty program name and percentage benefit",
            },
            {
              programName: loyaltyProgram.name,
              percentage: loyaltyProgram.percentage,
              hasPercentage: !!loyaltyProgram.percentage,
              b: (chunks) => <span className="font-medium">{chunks}</span>,
            },
          )}
          icon={<IconCheck className="size-6" />}
        />
      )}

      {!!priorityText && (
        <FlightFareCardSectionItem
          label={priorityText}
          icon={<IconCheck className="size-6" />}
        />
      )}

      {!!loungeAccess && (
        <FlightFareCardSectionItem
          label={intl.formatMessage({
            defaultMessage: "Lounge access",
            description:
              "Text indicating that a flight fare includes lounge access as a benefit",
          })}
          icon={<IconCheck className="size-6" />}
        />
      )}
    </FlightFareCardSection>
  );
}
